import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    // WCAG 2.2.2: keyboard users get the same pause-on-focus as mouse users.
    this.addEventListener('focusin', this.suspend);
    this.addEventListener('focusout', this.#handleFocusOut);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.#pauseButton = this.querySelector('.announcement-bar__pause');
    this.#pauseButton?.addEventListener('click', this.#togglePaused);

    // Hidden slides must not be reachable by keyboard (WCAG 2.4.3 / 4.1.2).
    this.current = this.#current;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.paused = true;
      this.#syncPauseButton();
      return;
    }

    this.play();
  }

  /** @type {HTMLButtonElement|null} */
  #pauseButton = null;

  #handleFocusOut = (/** @type {FocusEvent} */ event) => {
    if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
    this.resume();
  };

  #togglePaused = () => {
    if (this.paused) {
      this.paused = false;
      this.play();
    } else {
      this.pause();
    }
    this.#syncPauseButton();
  };

  #syncPauseButton() {
    // Rotating content is announced only when the user has paused it.
    this.setAttribute('aria-live', this.paused || !this.autoplay ? 'polite' : 'off');
    if (!this.#pauseButton) return;
    this.#pauseButton.setAttribute('aria-pressed', `${this.paused}`);
    this.#pauseButton.setAttribute(
      'aria-label',
      this.paused ? this.#pauseButton.dataset.playLabel || 'Play' : this.#pauseButton.dataset.pauseLabel || 'Pause'
    );
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;
    this.#syncPauseButton();
    clearInterval(this.#interval);

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    this.refs.slides?.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
      slide.inert = index !== relativeIndex;
    });
  }

  /**
   * Stop the timer while the page is hidden and restart it on return. Leaving the tab is not a visitor's pause:
   * pause() here set the paused flag, so the bar stayed stopped on return while its button still said Pause, and
   * the next click only un-paused it. A bar the visitor paused stays paused (resume() checks the flag).
   */
  #handleVisibilityChange = () => (document.hidden ? this.suspend() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
